import React, { ComponentProps } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { notNil } from '../../core/helpers/is-nil';
import { useMaxViewport } from '../../core/hooks/use-max-viewport';
import { Icon } from '../icon/icon';
import { ProductLine } from '../product-line/product-line';
import { Typography } from '../typography/typography';
import { usePathBuilder } from '../../core/routing/hooks/use-path-builder';
import { ArticleThumbnail } from '../article-thumbnail/article-thumbnail';
import styles from './product-card.module.scss';

type Props = {
  articleId: string;
  title: string;
  itemDesc2: string;
  itemDesc3: string;
  gloss: string | undefined;
  surface: string | undefined;
  effect: string | undefined;
  bonding: string | undefined;
  hexCode: string | undefined;
  weatherResistance: string | undefined;
  hasRendering: boolean;
} & ComponentProps<typeof ProductLine>;

export const ProductCard: React.FC<Props> = ({
  articleId,
  series,
  seriesNumber,
  seriesNumberColor,
  title,
  itemDesc2,
  itemDesc3,
  gloss,
  surface,
  effect,
  bonding,
  hexCode,
  weatherResistance,
  hasRendering,
}) => {
  const isTablet = useMaxViewport('s');
  const { articlePath } = usePathBuilder();
  const { t } = useTranslation();

  const effectBondingString = notNil(effect) && notNil(bonding) ? `${effect}, ${bonding}` : effect;
  const productProperties = [surface, gloss, weatherResistance, effectBondingString].filter((property) =>
    notNil(property),
  );

  const target = articlePath(articleId);

  return (
    <div className={styles.root}>
      <Link className={styles.topButton} to={target}>
        <ArticleThumbnail articleId={articleId} hasRendering={hasRendering} hexCode={hexCode} />
        <div className={styles.productLineContainer}>
          <ProductLine series={series} seriesNumber={seriesNumber} seriesNumberColor={seriesNumberColor} />
        </div>
        <div className={styles.textContainer}>
          <div className={styles.mainInformation}>
            <Typography variant="maintext">{title}</Typography>
            <Typography variant="headline-3">{itemDesc2}</Typography>
            <Typography className={styles.marginTop} variant="smalltext">
              {itemDesc3}
            </Typography>
          </div>
          <div className={styles.secondaryInformation}>
            {productProperties.map((property, index) => {
              const showDivider = index < productProperties.length - 1;
              return (
                <span key={property} className={styles.propertyContainer}>
                  <Typography variant="smalltext">{property}</Typography>
                  {showDivider ? <div className={styles.divider} /> : null}
                </span>
              );
            })}
          </div>
        </div>
      </Link>
      <Link aria-label={t('a11y.addArticle')} className={styles.orderButton} to={target}>
        <div className={styles.cartContainer}>
          <div className={styles.slidingBackground} />
          <Icon className={styles.cartIcon} size={isTablet ? '24' : '32'} variant="cart" />
        </div>
      </Link>
    </div>
  );
};
